import { render, h } from 'preact'
import { effect } from '@preact/signals'
import { DirectUpload } from "@rails/activestorage"
import Uppy from "@uppy/core"
import DropTarget from "@uppy/drop-target"
import { createPhotoSwipeLightbox } from '../../lib/photoswipe-lightbox.js'
import { createDriveStore } from './drive-store.js'
import { DriveApp } from '../../components/drive/DriveApp.jsx'
import { 
  rails_direct_uploads_path,
  drive_path,
  drive_index_path,
  drive_delete_file_path,
  drive_download_file_path,
  drive_get_file_permissions_path,
  drive_set_file_permissions_path,
  drive_get_folder_permissions_path,
  drive_set_folder_permissions_path,
  drive_create_share_path,
  drive_create_folder_share_path,
  files_delete_multiple_drive_index_path,
  files_move_drive_index_path,
  drive_download_folder_path,
  drive_attach_folder_uploads_path,
  attach_uploads_drive_index_path,
  folders_drive_index_path,
  share_targets_drive_index_path
} from "../../routes"
import * as bootstrap from "bootstrap"
import { I18n } from 'i18n-js'
import locales from '../../locales.json'
import { parseQuotaResponse } from '../../components/shared/quota/parseQuotaError.js'

/**
 * DriveHost — browse host for keyboard, Uppy upload, delete/download.
 * UI chrome + modals are Preact (DriveApp). This class no longer owns ERB targets.
 */
export class DriveSession {
  constructor(element, options = {}) {
    this.element = element
    this.options = options
    this.store = options.store || null
    this.i18n = options.i18n || null
    this.canUpdateValue = !!options.canUpdate
    this.canTagFilesValue = !!options.canTagFiles
    this.currentFolderValue = options.currentFolderId ? String(options.currentFolderId) : ''
    this.folderIdValue = this.currentFolderValue
    this.maxFileSizeValue = options.maxFileSize
    this.maxFilesValue = options.maxFiles
    this.skipRender = options.skipRender !== false
  }

  get dropzoneTarget() {
    return this.element.querySelector('[data-drive-dropzone="1"]') || this.element
  }

  get hasDropzoneTarget() {
    return !!this.element.querySelector('[data-drive-dropzone="1"]')
  }

  start() {
    if (!this.i18n) {
      this.i18n = new I18n(locales)
      this.i18n.locale = document.querySelector("meta[name='locale']")?.content || 'hu'
    }
    if (!this.store) {
      this.store = createDriveStore()
    }
    this.element._driveStore = this.store
    window._driveStore = this.store
    window._driveHost = this

    if (!this.skipRender) {
      this._preactRoot = document.getElementById('drive-preact-root') || this.element
      render(h(DriveApp, {
        store: this.store,
        i18n: this.i18n,
        host: this,
        canUpdate: this.canUpdateValue,
        canTagFiles: this.canTagFilesValue
      }), this._preactRoot)
    }

    this._onOpenMove = () => {
      window._driveUi?.openMove?.()
    }
    document.addEventListener('drive:open-move', this._onOpenMove)

    this.store.loadFolder(window.location.pathname + '.json' + window.location.search)

    this._onPopstate = () => {
      this.store.loadFolder(window.location.pathname + '.json')
    }
    window.addEventListener('popstate', this._onPopstate)

    this._folderSyncDisposer = effect(() => {
      const folder = this.store.currentFolder.value
      const id = this._numericFolderId(folder?.id)
      this.currentFolderValue = id || ''
      if (id) this.element.dataset.currentFolderId = id
      else delete this.element.dataset.currentFolderId
      this.element.classList.toggle('drive-virtual-folder', this._isVirtualFolderContext(folder))
    })

    this.selectedItems = new Set()
    this.uploadingFiles = new Map()
    this.shouldCancelUpload = false
    this._attachInProgress = false
    this.uploadedFiles = new Map()
    this.totalFiles = 0
    this.completedFiles = 0
    this.failedFiles = 0
    this.isUploading = false

    // Uppy needs dropzone in DOM — DriveApp mounts first in mount.js
    queueMicrotask(() => {
      this.uppy = this._createUppy()
      this.lightbox = createPhotoSwipeLightbox(this.element)
    })

    this.initializeKeyboardNavigation()
    this.initializeTableSelection()

    this._selectionDisposer = effect(() => {
      const ids = this.store.selectedIds.value
      this.selectedItems = new Set(ids)
      queueMicrotask(() => this._restoreKeyboardFocus())
    })

    this._loadingDisposer = effect(() => {
      const loading = this.store.loading.value
      if (loading) return
      queueMicrotask(() => {
        if (this._kbdFocus && !this._findRow(this._kbdFocus.type, this._kbdFocus.id)) {
          this._kbdFocus = null
        }
        if (!this._kbdFocus) this.initializeFocus()
        else this._restoreKeyboardFocus()
      })
    })
  }

  _bindDomActions() {
    this._onDomClick = (event) => this._dispatchDomAction(event, 'click')
    this._onDomChange = (event) => this._dispatchDomAction(event, 'change')
    this.element.addEventListener('click', this._onDomClick)
    this.element.addEventListener('change', this._onDomChange)
    if (this.toolbarElement) {
      this.toolbarElement.addEventListener('click', this._onDomClick)
    }
  }

  _dispatchDomAction(event, eventName) {
    const el = event.target.closest('[data-action]')
    if (!el || (!this.element.contains(el) && !(this.toolbarElement && this.toolbarElement.contains(el)))) return
    const parts = el.getAttribute('data-action').split(/\s+/)
    for (const part of parts) {
      const m = part.match(new RegExp(`^(?:${eventName}->)?(?:drive|drive-toolbar)#(\\w+)$`))
      if (!m) continue
      const method = m[1]
      // Map toolbar methods onto session
      const toolbarMap = {
        uploadFiles: 'uploadFiles',
        uploadFolder: 'triggerFolderInput',
        clearSelection: 'clearSelection',
        downloadSelected: 'downloadSelected',
        shareSelected: 'shareSelected',
        openMoveModal: 'openMoveModal',
        deleteSelected: 'deleteSelected'
      }
      const resolved = toolbarMap[method] || method
      if (typeof this[resolved] === 'function') {
        // Stimulus params: data-drive-filter-type-param → event.params.filterType
        const params = {}
        for (const attr of el.attributes) {
          const pm = attr.name.match(/^data-drive-(.+)-param$/)
          if (!pm) continue
          const key = pm[1].replace(/-([a-z])/g, (_, c) => c.toUpperCase())
          params[key] = attr.value
        }
        const fakeEvent = Object.assign(event, { params, preventDefault: () => event.preventDefault() })
        this[resolved](fakeEvent)
      }
    }
  }

  setupDragEventHandlers() {
    // No-op: drag handling is managed by Uppy DropTarget plugin
  }

  setupTurboStreamHandlers() {
    // Listen for form submit completion
    this.element.addEventListener('turbo:submit-end', (event) => {
      // Check if this is the new folder form
      if (event.target.closest('#newFolderModal') && event.detail.success) {
        // Close modal after successful submission
        setTimeout(() => {
          const modal = bootstrap.Modal.getInstance(document.getElementById('newFolderModal'))
          if (modal) {
            modal.hide()
          }
        }, 300)
      }
      
      // Check if this is the file upload form
      if (event.target === this.formTarget && event.detail.success) {
        // Upload form submitted successfully, Turbo Stream will update the file list
        // Hide placeholder if it exists
        this.hidePlaceholderIfNeeded()
      }
    })
    
    // Listen for modal shown event to reset form
    const modalElement = document.getElementById('newFolderModal')
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', (event) => {
        // Block new DriveFolder under BC/tasks/equipment virtual folders (would land at root).
        if (this._isVirtualFolderContext(this.store?.currentFolder?.value)) {
          event.preventDefault()
          window.alert(this.i18n.t('drive.virtual_folder_no_subfolders'))
        }
      })

      modalElement.addEventListener('shown.bs.modal', () => {
        // Reset form when modal is fully shown
        const form = modalElement.querySelector('form')
        if (form) {
          form.reset()
        }
        
        // Clear errors
        const errorsDiv = document.getElementById('folder-form-errors')
        if (errorsDiv) {
          errorsDiv.innerHTML = ''
        }
        
        // Set parent folder ID after reset (store-backed; survives SPA navigation)
        setTimeout(() => {
          const currentFolderId = this._resolveCurrentFolderId()
          const parentIdInput = modalElement.querySelector('input[name="drive_folder[parent_id]"]')

          if (parentIdInput) {
            parentIdInput.value = currentFolderId || ''
          }
        }, 10)
        
        // Focus on name input
        const nameInput = modalElement.querySelector('input[name="drive_folder[name]"]')
        if (nameInput) {
          nameInput.focus()
        }
      })
    }
  }
  
  hidePlaceholderIfNeeded() {
    // Find and hide empty state placeholder
    const placeholder = this.element.querySelector('.empty-state, .text-center.text-muted')
    if (placeholder) {
      placeholder.style.display = 'none'
    }
  }

  stop() {
    if (this._onOpenMove) {
      document.removeEventListener('drive:open-move', this._onOpenMove)
      this._onOpenMove = null
    }
    if (this._folderDropHandler && this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener('drop', this._folderDropHandler)
    }
    if (this.uppy) {
      this.uppy.destroy()
      this.uppy = null
    }

    if (this._onPopstate) {
      window.removeEventListener('popstate', this._onPopstate)
    }

    if (this._folderSyncDisposer) this._folderSyncDisposer()
    if (this._selectionDisposer) this._selectionDisposer()
    if (this._loadingDisposer) this._loadingDisposer()
    if (this._boundKeydown) {
      document.removeEventListener('keydown', this._boundKeydown)
      this._boundKeydown = null
    }
    if (this._boundRowPointer) {
      this.element.removeEventListener('click', this._boundRowPointer)
      this._boundRowPointer = null
    }
    if (this.store?.stopPreviewPolling) this.store.stopPreviewPolling()

    // Legacy path only — mount.js owns Preact when skipRender is true
    if (!this.skipRender && this._preactRoot) render(null, this._preactRoot)

    if (this.lightbox) {
      this.lightbox.destroy()
      this.lightbox = null
    }

    if (window._driveStore === this.store) window._driveStore = null
    if (window._driveHost === this) window._driveHost = null
    if (this.element._driveStore === this.store) this.element._driveStore = null
  }

  /** Public host API used by DriveApp / FileRow */
  uploadPlainFiles(files) {
    return this._uploadPlainFiles(files)
  }

  uploadFolderFromInput(event) {
    return this.uploadFolder(event)
  }

  deleteFileById(fileId) {
    return this.deleteFile({ params: { fileId } })
  }

  deleteFolderById(folderId) {
    return this.deleteFolder({ params: { folderId } })
  }

  // Private - Not needed, CSS handles hiding the input
  // hideFileInput() {
  //   this.inputTarget.disabled = true
  //   this.inputTarget.style.display = "none"
  // }

  _createUppy() {
    const uppy = new Uppy({ autoProceed: false })

    // Register our drop handler BEFORE uppy.use(DropTarget) so it fires first
    // (DOM listeners on the same element fire in registration order).
    // We intercept directory drops here and call stopImmediatePropagation()
    // to prevent Uppy from also receiving the event.
    // DataTransferItem.webkitGetAsEntry() must be called synchronously inside
    // the drop event — Uppy calls getDroppedFiles() asynchronously, so by the
    // time Uppy's onDrop callback fires the entries would be invalidated.
    this._folderDropHandler = async (e) => {
      if (!e.dataTransfer?.items) return

      // Collect entries synchronously — they're invalidated after the event
      const entries = Array.from(e.dataTransfer.items)
        .map(item => item.webkitGetAsEntry?.())
        .filter(Boolean)

      const hasDirectory = entries.some(entry => entry.isDirectory)
      if (!hasDirectory) return // let Uppy handle plain file drops

      e.preventDefault()
      e.stopImmediatePropagation() // blocks Uppy's drop listener (registered after ours)
      this.dropzoneTarget.classList.remove('drag-over')

      try {
        await this._uploadDroppedFolderEntries(entries)
      } catch (err) {
        console.error('Folder upload error:', err)
      }
    }
    this.dropzoneTarget.addEventListener('drop', this._folderDropHandler)

    uppy.use(DropTarget, {
      target: this.dropzoneTarget,
      onDragOver:  () => this.dropzoneTarget.classList.add('drag-over'),
      onDragLeave: () => this.dropzoneTarget.classList.remove('drag-over'),
      onDrop: (e) => {
        // Plain file drop: Uppy has added the files to its internal queue via
        // getDroppedFiles() before calling this callback. Read them from state,
        // remove from Uppy queue, and upload via our DirectUpload pipeline.
        this.dropzoneTarget.classList.remove('drag-over')
        const uppyFiles = uppy.getFiles()
        if (!uppyFiles.length) return
        const files = uppyFiles.map(f => f.data)
        uppyFiles.forEach(f => uppy.removeFile(f.id))
        this._uploadPlainFiles(files)
      }
    })

    return uppy
  }

  async _uploadPlainFiles(files) {
    if (this.shouldCancelUpload) return
    this.initializeUpload(files)
    const signedIds = []
    // Prevent uploadComplete from closing the modal before server attach finishes.
    this._attachInProgress = true

    try {
      for (const file of files) {
        if (this.shouldCancelUpload) break
        const fileId = file.fileId
        this.updateFileProgress(fileId, 0, 'uploading')
        try {
          const blob = await this._directUploadFile(file, fileId)
          signedIds.push(blob.signed_id)
          this.completeFile(fileId, true)
        } catch (err) {
          console.error('Upload failed:', err)
          this.completeFile(fileId, false)
        }
      }

      if (signedIds.length && !this.shouldCancelUpload) {
        this.store?.setUploadState?.({ serverProcessing: true })
        await this._attachSignedIdsViaFetch(signedIds)
      }
    } finally {
      this._attachInProgress = false
      this.store?.setUploadState?.({ serverProcessing: false })
      this.closeUploadModal()
      this.uploadedFiles.clear()
      this.totalFiles = 0
      this.completedFiles = 0
      this.failedFiles = 0
    }
  }

  async _attachSignedIdsViaFetch(signedIds) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const bcId = this._resolveBuildingComplexId()
    const currentFolderId = this._resolveCurrentFolderId()

    // Build form data matching what the form submission would have sent
    const formData = new FormData()
    signedIds.forEach(id => formData.append('drive_files[]', id))
    // Real DriveFolder only — BC virtual folders use a dedicated attach endpoint.
    if (!bcId && currentFolderId) formData.append('folder_id', currentFolderId)

    try {
      const response = await fetch(this._attachUploadsUrl(), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: formData
      })
      if (response.ok) {
        // Reload the full folder to get all file metadata (preview_url, blob_id, editor_supported, etc.)
        if (this.store) {
          const folderPath = window.location.pathname + '.json' + window.location.search
          await this.store.loadFolder(folderPath)
        }
        this.hidePlaceholderIfNeeded()
      } else {
        const quota = await parseQuotaResponse(response)
        if (quota) {
          this.onQuotaExceeded?.(quota.raw ?? quota)
          window._driveUi?.reportQuotaExceeded?.(quota.raw ?? quota)
          return
        }
        console.error('Attach failed:', response.status)
      }
    } catch (err) {
      console.error('Attach request failed:', err)
    }
  }

  _directUploadFile(file, fileId) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, rails_direct_uploads_path(), {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener('progress', (ev) => {
            if (ev.lengthComputable) {
              const pct = Math.round((ev.loaded / ev.total) * 95)
              this.updateFileProgress(fileId, pct, 'uploading')
            }
          })
        }
      })
      upload.create((error, blob) => error ? reject(error) : resolve(blob))
    })
  }

  _attachSignedIds(signedIds) {
    this.formTarget.querySelectorAll('input[type="hidden"][name="drive_files[]"]').forEach(el => el.remove())
    signedIds.forEach(id => {
      const input = document.createElement('input')
      input.type  = 'hidden'
      input.name  = 'drive_files[]'
      input.value = id
      this.inputTarget.insertAdjacentElement('beforebegin', input)
    })
  }

  removeElement(el) {
    if (el && el.parentNode) {
      el.parentNode.removeChild(el)
    }
  }

  initializeKeyboardNavigation() {
    // Listen on document — arrows must work even when focus is not inside a row
    // (e.g. after Preact re-render, or when user just opened Drive).
    this._boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this._boundKeydown)

    // Keep keyboard cursor in sync when user clicks a row
    this._boundRowPointer = (event) => {
      const row = event.target.closest?.('tbody tr[tabindex]')
      if (!row || !this.element.contains(row)) return
      this._kbdFocus = { type: row.dataset.itemType, id: row.dataset.itemId }
      this.element.querySelectorAll('tbody tr.drive-row-focused').forEach((el) => {
        el.classList.remove('drive-row-focused')
      })
      row.classList.add('drive-row-focused')
    }
    this.element.addEventListener('click', this._boundRowPointer)
  }

  // Table selection is owned by Preact FileRow/FolderRow (store.selectItem).
  initializeTableSelection() {
    // no-op — legacy Stimulus handlers conflicted with Preact
  }

  // Initialize focus on first row
  initializeFocus() {
    const firstRow = this.element.querySelector('tbody tr[tabindex]')
    if (firstRow) this._setKeyboardFocus(firstRow)
  }

  _keyboardRows() {
    return Array.from(this.element.querySelectorAll('tbody tr[tabindex]'))
  }

  _findRow(type, id) {
    if (type == null || id == null) return null
    return this.element.querySelector(
      `tbody tr[data-item-type="${CSS.escape(String(type))}"][data-item-id="${CSS.escape(String(id))}"]`
    )
  }

  _setKeyboardFocus(row) {
    this.element.querySelectorAll('tbody tr.drive-row-focused').forEach((el) => {
      el.classList.remove('drive-row-focused')
    })

    if (!row) {
      this._kbdFocus = null
      return
    }

    this._kbdFocus = {
      type: row.dataset.itemType,
      id: row.dataset.itemId
    }
    row.classList.add('drive-row-focused')
    if (typeof row.focus === 'function') {
      row.focus({ preventScroll: true })
    }
    row.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  _restoreKeyboardFocus() {
    if (!this._kbdFocus) return
    const row = this._findRow(this._kbdFocus.type, this._kbdFocus.id)
    if (!row) return
    row.classList.add('drive-row-focused')
    if (document.activeElement !== row && typeof row.focus === 'function') {
      row.focus({ preventScroll: true })
    }
  }

  _focusedRowFromEvent(event) {
    const fromTarget = event.target?.closest?.('tbody tr[tabindex]')
    if (fromTarget && this.element.contains(fromTarget)) return fromTarget
    if (this._kbdFocus) {
      return this._findRow(this._kbdFocus.type, this._kbdFocus.id)
    }
    return this.element.querySelector('tbody tr.drive-row-focused, tbody tr:focus')
  }

  // Toggle selection for individual items (legacy)
  toggleSelectItem(event) {
    const checkbox = event.target
    const row = checkbox.closest('tr')
    const itemId = row?.dataset.itemId
    const itemType = row?.dataset.itemType
    if (!itemId || !itemType || itemType === 'back') return
    event.stopPropagation()
    if (this.store) this.store.selectItem(`${itemType}-${itemId}`, true)
  }

  // Select/deselect via Preact store (Space / S)
  selectRow(row) {
    const itemId = row.dataset.itemId
    const itemType = row.dataset.itemType
    if (!itemId || !itemType || itemType === 'back') return
    if (this.store) this.store.selectItem(`${itemType}-${itemId}`, true)
    this._setKeyboardFocus(row)
  }

  // Toggle select all
  toggleSelectAll(event) {
    const selectAllCheckbox = event.target
    // Get checkboxes from ALL tbody elements (folders and files)
    const checkboxes = this.element.querySelectorAll('tbody input[type="checkbox"]:not([disabled])')

    checkboxes.forEach(checkbox => {
      checkbox.checked = selectAllCheckbox.checked
      const row = checkbox.closest('tr')
      const itemId = row.dataset.itemId
      const itemType = row.dataset.itemType

      if (itemId && itemType) {
        if (selectAllCheckbox.checked) {
          this.selectedItems.add(`${itemType}_${itemId}`)
          row.classList.add('selected')
          row.setAttribute('aria-selected', 'true')
        } else {
          this.selectedItems.delete(`${itemType}_${itemId}`)
          row.classList.remove('selected')
          row.setAttribute('aria-selected', 'false')
        }
      }
    })

    this.updateActionBar()
  }


  // File/Folder selection
  selectItem(event) {
    const item = event.currentTarget
    const itemId = item.dataset.itemId
    const itemType = item.dataset.itemType

    if (event.shiftKey && this.lastSelectedItem) {
      // Range selection
      this.selectRange(this.lastSelectedItem, item)
    } else if (event.ctrlKey || event.metaKey) {
      // Toggle selection
      this.toggleSelection(item, itemId, itemType)
    } else {
      // Single selection (clear others)
      this.clearSelection()
      this.toggleSelection(item, itemId, itemType)
    }

    this.lastSelectedItem = item
    this.updateActionBar()
  }

  toggleSelection(item, itemId, itemType) {
    const key = `${itemType}-${itemId}`
    
    if (this.selectedItems.has(key)) {
      this.selectedItems.delete(key)
      item.classList.remove("selected")
    } else {
      this.selectedItems.add(key)
      item.classList.add("selected")
    }
  }

  selectRange(startItem, endItem) {
    const allItems = this.getAllSelectableItems()
    const startIndex = allItems.indexOf(startItem)
    const endIndex = allItems.indexOf(endItem)
    
    const [min, max] = startIndex < endIndex 
      ? [startIndex, endIndex] 
      : [endIndex, startIndex]

    for (let i = min; i <= max; i++) {
      const item = allItems[i]
      const itemId = item.dataset.itemId
      const itemType = item.dataset.itemType
      const key = `${itemType}-${itemId}`
      
      if (!this.selectedItems.has(key)) {
        this.selectedItems.add(key)
        item.classList.add("selected")
      }
    }
  }

  clearSelection() {
    // Clear the Preact signals store selection — this will trigger the effect()
    // which in turn syncs selectedItems and updates the action bar
    if (this.store) {
      this.store.clearSelection()
    }

    this.selectedItems.clear()
    this.getAllSelectableItems().forEach(item => {
      item.classList.remove("selected")
    })
    
    // Also clear table row selections
    const tableRows = this.element.querySelectorAll('tbody tr')
    tableRows.forEach(row => {
      row.classList.remove('selected')
      row.setAttribute('aria-selected', 'false')
      const checkbox = row.querySelector('input[type="checkbox"]')
      if (checkbox) {
        checkbox.checked = false
      }
    })
    
    // Clear select all checkbox
    const selectAllCheckbox = this.element.querySelector('thead input[type="checkbox"]')
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
    }
    this.updateActionBar()
  }

  selectAll(event) {
    event.preventDefault()
    
    // Select all table rows — collect all IDs for Preact store update
    const allIds = []
    const tableRows = this.element.querySelectorAll('tbody tr[data-item-id][data-item-type]')
    tableRows.forEach(row => {
      const itemId = row.dataset.itemId
      const itemType = row.dataset.itemType
      if (itemId && itemType) {
        const key = `${itemType}-${itemId}`
        allIds.push(key)
        this.selectedItems.add(key)
        row.classList.add('selected')
        row.setAttribute('aria-selected', 'true')
        
        const checkbox = row.querySelector('input[type="checkbox"]')
        if (checkbox) {
          checkbox.checked = true
        }
      }
    })

    // Also update Preact store so action buttons and action bar are in sync
    if (this.store && allIds.length > 0) {
      this.store.selectedIds.value = allIds
    }
    
    // Update select all checkbox
    const selectAllCheckbox = this.element.querySelector('thead input[type="checkbox"]')
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = true
      selectAllCheckbox.indeterminate = false
    }
    
    this.updateActionBar()
  }

  deselectAll(event) {
    event.preventDefault()
    this.clearSelection()
    this.updateActionBar()
  }

  getAllSelectableItems() {
    return this._keyboardRows()
  }

  // Keyboard navigation
  handleKeydown(event) {
    if (!this.element.isConnected) return
    // Don't steal keys from inputs, contenteditables, or open Bootstrap modals
    if (event.target.matches('input, textarea, select, [contenteditable="true"]')) return
    if (document.querySelector('.modal.show')) return

    const rows = this._keyboardRows()

    // Arrow keys - move keyboard cursor (visible focus ring)
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (rows.length === 0) return
      event.preventDefault()
      event.stopPropagation()

      const currentRow = this._focusedRowFromEvent(event)
      let currentIndex = currentRow ? rows.indexOf(currentRow) : -1
      let newIndex

      if (event.key === 'ArrowDown') {
        newIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, rows.length - 1)
      } else {
        newIndex = currentIndex === -1 ? rows.length - 1 : Math.max(currentIndex - 1, 0)
      }

      const newRow = rows[newIndex]
      if (newRow) this._setKeyboardFocus(newRow)
      return
    }

    // Enter - Open file or navigate to folder (SPA)
    if (event.key === 'Enter') {
      const focusedRow = this._focusedRowFromEvent(event)
      if (!focusedRow || !this.element.contains(focusedRow)) return

      event.preventDefault()
      event.stopPropagation()
      const itemType = focusedRow.dataset.itemType

      if (itemType === 'back') {
        focusedRow.click()
        return
      }

      if (itemType === 'folder') {
        const folderLink = focusedRow.querySelector('td a')
        if (folderLink && this.store) {
          const path = folderLink.getAttribute('href')
          this._kbdFocus = null
          history.pushState({}, '', path)
          this.store.loadFolder(`${path}.json`)
        }
        return
      }

      if (itemType === 'file') {
        const fileLink = focusedRow.querySelector('td a')
        if (fileLink) fileLink.click()
      }
      return
    }

    // Space / S - Toggle selection of focused item
    if (event.key === ' ' || event.key === 's' || event.key === 'S') {
      const focusedRow = this._focusedRowFromEvent(event)
      if (!focusedRow || !this.element.contains(focusedRow)) return
      event.preventDefault()
      event.stopPropagation()
      this.selectRow(focusedRow)
      return
    }

    // Ctrl/Cmd + A - Select all
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      event.preventDefault()
      this.selectAll(event)
      return
    }

    // Escape - Clear selection
    if (event.key === 'Escape') {
      event.preventDefault()
      this.clearSelection()
      return
    }

    // Delete / Backspace - Delete selected items
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (this.selectedItems.size > 0) {
        event.preventDefault()
        event.stopPropagation()
        this.deleteSelected()
      }
    }
  }

  handleArrowKey(event) {
    if (event.target.matches('input, textarea')) return

    const items = this.getAllSelectableItems()
    const currentFocused = document.activeElement
    const currentIndex = items.indexOf(currentFocused)

    if (currentIndex === -1) {
      // No item focused, focus first item
      if (items.length > 0) {
        items[0].focus()
      }
      return
    }

    let nextIndex = currentIndex
    const itemsPerRow = 6 // Approximate, adjust based on grid layout

    switch(event.key) {
      case 'ArrowRight':
        nextIndex = Math.min(currentIndex + 1, items.length - 1)
        break
      case 'ArrowLeft':
        nextIndex = Math.max(currentIndex - 1, 0)
        break
      case 'ArrowDown':
        nextIndex = Math.min(currentIndex + itemsPerRow, items.length - 1)
        break
      case 'ArrowUp':
        nextIndex = Math.max(currentIndex - itemsPerRow, 0)
        break
    }

    if (nextIndex !== currentIndex) {
      event.preventDefault()
      items[nextIndex].focus()

      if (event.shiftKey) {
        // Extend selection
        const itemId = items[nextIndex].dataset.itemId
        const itemType = items[nextIndex].dataset.itemType
        this.toggleSelection(items[nextIndex], itemId, itemType)
        this.updateActionBar()
      }
    }
  }

  // Adaptive header toolbar + legacy select-all checkbox state
  updateActionBar() {
    const count = this.store
      ? this.store.selectedIds.value.length
      : this.selectedItems.size

    if (this.hasDriveToolbarOutlet) {
      this.driveToolbarOutlet.setSelectionMode(count)
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count
    }

    // Update select all checkbox state
    const selectAllCheckbox = this.element.querySelector('thead input[type="checkbox"]')
    if (selectAllCheckbox) {
      // Get checkboxes from ALL tbody elements (folders and files)
      const checkboxes = this.element.querySelectorAll('tbody input[type="checkbox"]:not([disabled])')
      const checkedCheckboxes = Array.from(checkboxes).filter(cb => cb.checked)
      
      if (checkedCheckboxes.length === 0) {
        selectAllCheckbox.checked = false
        selectAllCheckbox.indeterminate = false
      } else if (checkedCheckboxes.length === checkboxes.length) {
        selectAllCheckbox.checked = true
        selectAllCheckbox.indeterminate = false
      } else {
        selectAllCheckbox.checked = false
        selectAllCheckbox.indeterminate = true
      }
    }
  }

  // Filter items by type
  filterByType(event) {
    const filterType = event.params.filterType || 'all'
    const folderRows = this.element.querySelectorAll('tbody tr[data-item-type="folder"]')
    const fileRows = this.element.querySelectorAll('tbody tr[data-item-type="file"]')
    
    // Update button states
    const buttons = this.element.querySelectorAll('[data-action*="filterByType"]')
    buttons.forEach(btn => btn.classList.remove('active'))
    event.currentTarget.classList.add('active')
    
    // Show/hide based on filter
    if (filterType === 'all') {
      folderRows.forEach(row => row.style.display = '')
      fileRows.forEach(row => row.style.display = '')
    } else if (filterType === 'folders') {
      folderRows.forEach(row => row.style.display = '')
      fileRows.forEach(row => row.style.display = 'none')
    } else if (filterType === 'files') {
      folderRows.forEach(row => row.style.display = 'none')
      fileRows.forEach(row => row.style.display = '')
    }
  }

  // Search functionality
  setupSearch() {
    if (!this.hasSearchInputTarget) return

    let searchTimeout
    this.searchInputTarget.addEventListener('input', (e) => {
      clearTimeout(searchTimeout)
      const value = e.target.value.trim()
      this._syncSearchClearVisibility()

      if (value.length > 0) {
        searchTimeout = setTimeout(() => {
          this.performSearch(value)
        }, 300)
      } else if (this._isSearchPage()) {
        // Empty field while viewing search results → leave search
        searchTimeout = setTimeout(() => {
          this.clearSearch()
        }, 300)
      }
    })

    this._syncSearchClearVisibility()
  }

  _isSearchPage() {
    return /\/drive\/search\/?$/.test(window.location.pathname)
  }

  _syncSearchClearVisibility() {
    if (!this.hasSearchClearTarget || !this.hasSearchInputTarget) return
    const hasQuery = this.searchInputTarget.value.trim().length > 0
    this.searchClearTarget.classList.toggle('d-none', !hasQuery)
  }

  // Clear search query and return to the folder (or Drive root) that was searched in.
  clearSearch(event) {
    event?.preventDefault()
    sessionStorage.removeItem('driveSearchTerm')
    sessionStorage.removeItem('driveSearchFocus')

    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.store) this.store.searchQuery.value = ''
    this._syncSearchClearVisibility()

    if (!this._isSearchPage()) return

    const folderId =
      this._resolveCurrentFolderId() ||
      new URLSearchParams(window.location.search).get('folder_id')

    window.location.href = folderId ? drive_path(folderId) : drive_index_path()
  }

  performSearch(query) {
    const searchTerm = query.trim()
    if (!searchTerm) {
      this.clearSearch()
      return
    }

    // Store the search term so we can restore focus after page reload
    sessionStorage.setItem('driveSearchTerm', searchTerm)
    sessionStorage.setItem('driveSearchFocus', 'true')

    const folderId = this._resolveCurrentFolderId()

    const params = new URLSearchParams()
    params.append('q', searchTerm)
    if (folderId) {
      params.append('folder_id', folderId)
    }

    window.location.href = drive_index_path() + '/search?' + params.toString()
  }

  restoreSearchFocus() {
    // Check if we should restore focus to search input after full-page search navigate
    const shouldFocus = sessionStorage.getItem('driveSearchFocus')
    const searchTerm = sessionStorage.getItem('driveSearchTerm')

    if (shouldFocus === 'true' && this.hasSearchInputTarget) {
      sessionStorage.removeItem('driveSearchFocus')
      sessionStorage.removeItem('driveSearchTerm')

      // Prefer server-rendered value; fall back to sessionStorage if input empty
      if (searchTerm && this.searchInputTarget.value.trim() === '') {
        this.searchInputTarget.value = searchTerm
      }

      if (this.store && this.searchInputTarget.value.trim() !== '') {
        this.store.searchQuery.value = this.searchInputTarget.value.trim()
      }

      if (this.searchInputTarget.value.trim() !== '') {
        setTimeout(() => {
          this.searchInputTarget.focus()
          const length = this.searchInputTarget.value.length
          this.searchInputTarget.setSelectionRange(length, length)
        }, 100)
      }
      this._syncSearchClearVisibility()
    }
  }

  // Actions
  deleteSelected() {
    // Use Preact store selection if available, fallback to Stimulus selectedItems
    const ids = this.store
      ? Array.from(this.store.selectedIds.value)
      : Array.from(this.selectedItems)

    if (ids.length === 0) return

    if (!confirm(`${this.i18n.t('drive.delete')} ${ids.length} ${this.i18n.t('drive.items_selected')}?`)) return

    // Store uses 'file-{id}' / 'folder-{id}' (dash separator) from Preact signals
    const fileIds = ids
      .filter(key => key.startsWith('file-'))
      .map(key => key.replace('file-', ''))

    const folderIds = ids
      .filter(key => key.startsWith('folder-'))
      .map(key => key.replace('folder-', ''))

    if (fileIds.length > 0) {
      this.deleteFiles(fileIds)
    }

    if (folderIds.length > 0) {
      this.deleteFolders(folderIds)
    }
  }

  goBack(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const backItem = event.currentTarget
    const link = backItem.querySelector('a')
    if (link) {
      window.location.href = link.href
    }
  }

  uploadFiles(event) {
    // Trigger the hidden file input
    if (this.hasInputTarget) {
      this.inputTarget.click()
    }
  }

  triggerFolderInput() {
    if (this.hasFolderInputTarget) {
      this.folderInputTarget.click()
    }
  }

  // Handle file input change (when user selects files)
  inputTargetConnected() {
    if (!this.hasInputTarget) return
    this.inputTarget.addEventListener('change', async (event) => {
      const files = Array.from(event.target.files)
      event.target.value = ''
      if (!files.length) return
      await this._uploadPlainFiles(files)
    })
  }

  // Note: openNewFolderModal is no longer needed, the modal reset is handled by shown.bs.modal event

  deleteFiles(fileIds) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const folderId = this._resolveCurrentFolderId()

    fetch(files_delete_multiple_drive_index_path(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ 
        file_ids: fileIds,
        folder_id: folderId
      })
    })
    .then(response => {
      if (response.ok) {
        if (this.store) {
          fileIds.forEach(id => this.store.removeFile(parseInt(id, 10)))
          this.store.clearSelection()
        }
        this.clearSelection()
      } else {
        throw new Error('Delete failed')
      }
    })
    .catch(error => {
      console.error('Error deleting files:', error)
      alert(this.i18n.t('drive.error'))
    })
  }

  deleteFolders(folderIds) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    const deletePromises = folderIds.map(folderId => 
      fetch(drive_path(folderId), {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        }
      })
      .then(response => {
        if (response.ok) {
          return folderId
        } else {
          throw new Error('Delete failed')
        }
      })
    )

    Promise.all(deletePromises)
    .then(deletedIds => {
      if (this.store) {
        deletedIds.forEach(id => this.store.removeFolder(parseInt(id, 10)))
        this.store.clearSelection()
      }
      this.clearSelection()
    })
    .catch(error => {
      console.error('Error deleting folders:', error)
      alert(this.i18n.t('drive.error'))
    })
  }

  moveSelected(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this._selectedKeys().length === 0) return

    const targetFolderId = event.params.folderId
    this._moveSelectionToFolder(targetFolderId)
  }

  moveFiles(fileIds, targetFolderId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    return fetch(files_move_drive_index_path(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        file_ids: fileIds,
        target_folder_id: targetFolderId
      })
    })
    .then(response => {
      if (!response.ok) throw new Error('Move failed')
      return response
    })
  }

  downloadSelected() {
    const ids = this._selectedKeys()
    if (ids.length === 0) return

    const urls = []
    ids.forEach(key => {
      if (key.startsWith('file-')) {
        urls.push(drive_download_file_path(key.replace('file-', '')))
      } else if (key.startsWith('folder-')) {
        urls.push(drive_download_folder_path(key.replace('folder-', '')))
      }
    })

    urls.forEach((url, index) => {
      setTimeout(() => this._triggerDownload(url), index * 250)
    })
  }

  shareSelected() {
    const ids = this._selectedKeys()
    if (ids.length !== 1) return

    const key = ids[0]
    if (key.startsWith('file-')) {
      this.shareFile({ params: { fileId: key.replace('file-', '') } })
    } else if (key.startsWith('folder-')) {
      this.shareFolder({ params: { folderId: key.replace('folder-', '') } })
    }
  }

  openMoveModal() {
    const ids = this._selectedKeys()
    if (ids.length === 0) return
    window._driveUi?.openMove?.()
  }

  confirmMoveToCurrentFolder(event) {
    event?.preventDefault()
    // Prefer highlighted destination folder; else current browsed location (incl. root).
    const targetFolderId = this._moveHighlightId != null
      ? this._moveHighlightId
      : (this._moveNavStack?.[this._moveNavStack.length - 1]?.id ?? null)
    this._moveSelectionToFolder(targetFolderId)
  }

  selectMoveFolder(event) {
    event.preventDefault()
    event.stopPropagation()
    const row = event.currentTarget.closest('[data-move-folder-id]')
    if (!row) return

    const folderId = row.dataset.moveFolderId
    this._moveHighlightId = folderId
    this._syncMoveFolderSelection()
    this._updateMoveConfirmState()
  }

  navigateMoveIntoFolder(event) {
    event.preventDefault()
    event.stopPropagation()
    const btn = event.currentTarget
    const folderId = btn.dataset.folderId
    const folderName = btn.dataset.folderName
    if (!folderId) return

    this._moveNavStack.push({ id: folderId, name: folderName })
    this._moveHighlightId = null
    this._loadMoveFolders(folderId)
  }

  navigateMoveBack(event) {
    event.preventDefault()
    this._moveNavStack.pop()
    this._moveHighlightId = null
    const parent = this._moveNavStack[this._moveNavStack.length - 1]
    this._loadMoveFolders(parent ? parent.id : null)
  }

  navigateMoveToBreadcrumb(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.navIndex, 10)
    if (Number.isNaN(index)) return

    if (index < 0) {
      this._moveNavStack = []
    } else {
      this._moveNavStack = this._moveNavStack.slice(0, index + 1)
    }
    this._moveHighlightId = null
    const parent = this._moveNavStack[this._moveNavStack.length - 1]
    this._loadMoveFolders(parent ? parent.id : null)
  }

  _selectedKeys() {
    if (this.store) return Array.from(this.store.selectedIds.value)
    return Array.from(this.selectedItems)
  }

  _triggerDownload(url) {
    const link = document.createElement('a')
    link.href = url
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  _updateMoveModalHeader(ids) {
    const names = this._moveItemNames(ids)
    if (this.hasMoveModalTitleTarget) {
      const title = names.length === 1
        ? this.i18n.t('drive.move_title', { name: names[0] })
        : this.i18n.t('drive.move_title_multiple', { count: ids.length })
      this.moveModalTitleTarget.textContent = title
    }

    if (this.hasMoveSourceLocationTarget) {
      const location = this._moveSourceLocationLabel()
      this.moveSourceLocationTarget.innerHTML = `
        <span>${this._escapeHtml(this.i18n.t('drive.move_current_location'))}</span>
        <span class="drive-move-modal__source-chip ms-1">
          <i class="bi bi-folder2 me-1"></i>${this._escapeHtml(location)}
        </span>`
    }

    this._updateMoveConfirmState()
  }

  _moveItemNames(ids) {
    const names = []
    for (const key of ids) {
      if (key.startsWith('folder-')) {
        const id = parseInt(key.replace('folder-', ''), 10)
        const folder = this.store?.folders?.value?.find(f => f.id === id)
        if (folder?.name) names.push(folder.name)
      } else if (key.startsWith('file-')) {
        const id = parseInt(key.replace('file-', ''), 10)
        const file = this.store?.files?.value?.find(f => f.id === id)
        if (file?.filename) names.push(file.filename)
      }
    }
    return names
  }

  _moveSourceLocationLabel() {
    const crumbs = this.store?.breadcrumbs?.value || []
    const current = this.store?.currentFolder?.value
    if (current?.name) return current.name
    if (crumbs.length > 0) return crumbs[crumbs.length - 1].name
    return this.i18n.t('drive.move_folder_root')
  }

  _updateMoveConfirmState() {
    if (!this.hasMoveConfirmBtnTarget) return
    // Always allow move to current browsed location (root or navigated folder),
    // or to an explicitly highlighted destination.
    this.moveConfirmBtnTarget.disabled = false
    const label = this._moveHighlightId != null
      ? this.i18n.t('drive.move')
      : this.i18n.t('drive.move_here')
    this.moveConfirmBtnTarget.textContent = label
  }

  _syncMoveFolderSelection() {
    if (!this.hasMoveFolderListTarget) return
    this.moveFolderListTarget.querySelectorAll('.drive-move-modal__row').forEach(row => {
      const selected = this._moveHighlightId != null
        && String(row.dataset.moveFolderId) === String(this._moveHighlightId)
      row.classList.toggle('is-selected', selected)
      row.setAttribute('aria-selected', selected ? 'true' : 'false')
    })

    if (this.hasMovePathHintTarget) {
      const selectedRow = this.moveFolderListTarget.querySelector('.drive-move-modal__row.is-selected')
      if (selectedRow) {
        const name = selectedRow.dataset.moveFolderName || ''
        const stackNames = (this._moveNavStack || []).map(s => s.name)
        const path = [...stackNames, name].filter(Boolean).join(' / ')
        this.movePathHintTarget.textContent = path
          ? `${this.i18n.t('drive.move_folder_root')} / ${path}`
          : this.i18n.t('drive.move_folder_root')
      } else {
        this.movePathHintTarget.textContent = this.i18n.t('drive.move_path_hint')
      }
    }
  }

  _loadMoveFolders(parentId) {
    this._renderMoveNavBar()
    this._updateMoveConfirmState()

    if (this.hasMoveFolderListTarget) {
      this.moveFolderListTarget.innerHTML = `
        <div class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        </div>`
    }

    const url = parentId
      ? `${folders_drive_index_path({ format: 'json' })}?parent_id=${encodeURIComponent(parentId)}`
      : folders_drive_index_path({ format: 'json' })

    fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      }
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(folders => this._renderMoveFolderList(folders))
      .catch(() => {
        if (this.hasMoveFolderListTarget) {
          this.moveFolderListTarget.innerHTML = `
            <div class="alert alert-danger m-3 mb-0">
              <i class="bi bi-exclamation-triangle me-2"></i>${this.i18n.t('drive.move_folder_load_error')}
            </div>`
        }
      })
  }

  _renderMoveNavBar() {
    if (!this.hasMoveFolderNavTarget) return

    const stack = this._moveNavStack || []
    const rootLabel = this.i18n.t('drive.move_folder_root')

    const crumbs = [
      `<button type="button" class="drive-move-modal__crumb btn btn-link btn-sm text-decoration-none px-1"
               data-action="click->drive#navigateMoveToBreadcrumb" data-nav-index="-1">
         <i class="bi bi-hdd me-1"></i>${this._escapeHtml(rootLabel)}
       </button>`
    ]

    stack.forEach((item, index) => {
      const isLast = index === stack.length - 1
      crumbs.push(`<span class="text-muted px-1">/</span>`)
      if (isLast) {
        crumbs.push(`<span class="drive-move-modal__crumb is-current px-1 fw-medium text-truncate">${this._escapeHtml(item.name)}</span>`)
      } else {
        crumbs.push(`
          <button type="button" class="drive-move-modal__crumb btn btn-link btn-sm text-decoration-none px-1 text-truncate"
                  data-action="click->drive#navigateMoveToBreadcrumb" data-nav-index="${index}">
            ${this._escapeHtml(item.name)}
          </button>`)
      }
    })

    this.moveFolderNavTarget.innerHTML = `
      <div class="drive-move-modal__crumbs d-flex align-items-center flex-nowrap px-3 py-2 overflow-auto">
        ${crumbs.join('')}
      </div>`
  }

  _renderMoveFolderList(folders) {
    if (!this.hasMoveFolderListTarget) return

    const blocked = this._moveBlockedFolderIds || new Set()
    const visible = (folders || []).filter(f => !blocked.has(String(f.id)))

    if (visible.length === 0) {
      this.moveFolderListTarget.innerHTML = `
        <div class="text-muted text-center py-4 small">${this.i18n.t('drive.move_folder_empty')}</div>`
      this._syncMoveFolderSelection()
      return
    }

    const rows = visible.map(folder => {
      const canOpen = folder.has_children
      return `
        <div class="drive-move-modal__row"
             role="option"
             tabindex="0"
             aria-selected="false"
             data-move-folder-id="${folder.id}"
             data-move-folder-name="${this._escapeAttr(folder.name)}"
             data-action="click->drive#selectMoveFolder dblclick->drive#navigateMoveIntoFolder"
             data-folder-id="${folder.id}"
             data-folder-name="${this._escapeAttr(folder.name)}">
          <div class="drive-move-modal__row-main text-truncate">
            <i class="bi bi-folder-fill text-warning me-2" aria-hidden="true"></i>
            <span class="fw-medium">${this._escapeHtml(folder.name)}</span>
          </div>
          <div class="drive-move-modal__row-actions">
            <button type="button"
                    class="btn btn-sm btn-dark drive-move-modal__move-btn"
                    data-action="click->drive#moveSelected"
                    data-drive-folder-id-param="${folder.id}">
              ${this.i18n.t('drive.move')}
            </button>
            ${canOpen ? `
              <button type="button"
                      class="btn btn-sm btn-outline-secondary drive-move-modal__open-btn"
                      title="${this._escapeAttr(this.i18n.t('drive.move_folder_open'))}"
                      aria-label="${this._escapeAttr(this.i18n.t('drive.move_folder_open'))}"
                      data-action="click->drive#navigateMoveIntoFolder"
                      data-folder-id="${folder.id}"
                      data-folder-name="${this._escapeAttr(folder.name)}">
                <i class="bi bi-chevron-right" aria-hidden="true"></i>
              </button>` : `
              <span class="drive-move-modal__open-spacer" aria-hidden="true"></span>`}
          </div>
        </div>`
    }).join('')

    this.moveFolderListTarget.innerHTML = rows
    this._syncMoveFolderSelection()
  }

  async _moveSelectionToFolder(targetFolderId) {
    const ids = this._selectedKeys()
    if (ids.length === 0) return

    const fileIds = ids
      .filter(key => key.startsWith('file-'))
      .map(key => key.replace('file-', ''))
    const folderIds = ids
      .filter(key => key.startsWith('folder-'))
      .map(key => key.replace('folder-', ''))

    // Block moving a folder into itself
    if (targetFolderId != null && folderIds.map(String).includes(String(targetFolderId))) {
      alert(this.i18n.t('drive.move_invalid_target'))
      return
    }

    try {
      const tasks = []
      if (fileIds.length > 0) {
        tasks.push(this.moveFiles(fileIds, targetFolderId))
      }
      folderIds.forEach(folderId => {
        tasks.push(this._moveFolder(folderId, targetFolderId))
      })

      await Promise.all(tasks)

      const modalEl = document.getElementById('driveMoveFolderModal')
      if (modalEl) {
        bootstrap.Modal.getInstance(modalEl)?.hide()
      }

      this.clearSelection()
      if (this.store) {
        await this.store.loadFolder(window.location.pathname + '.json' + window.location.search)
      }
    } catch (error) {
      console.error('Error moving items:', error)
      alert(this.i18n.t('drive.error'))
    }
  }

  _moveFolder(folderId, targetFolderId) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    return fetch(drive_path(folderId, { format: 'json' }), {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        drive_folder: { parent_id: targetFolderId }
      })
    }).then(response => {
      if (!response.ok) throw new Error('Folder move failed')
      return response
    })
  }

  _escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  _escapeAttr(value) {
    return this._escapeHtml(value).replace(/'/g, '&#39;')
  }

  // Share file
  async shareFile(event) {
    const fileId = event.params.fileId
    window._driveUi?.openShare?.({ type: 'file', id: fileId })
  }

  // Share folder
  async shareFolder(event) {
    const folderId = event.params.folderId
    window._driveUi?.openShare?.({ type: 'folder', id: folderId })
  }

  // Lazy-load people/groups into share + permissions selects (once per page).
  async ensureShareTargetsLoaded() {
    if (this._shareTargetsLoaded) return
    if (this._shareTargetsPromise) return this._shareTargetsPromise

    this._shareTargetsPromise = (async () => {
      const res = await fetch(share_targets_drive_index_path({ format: 'json' }), {
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      if (!res.ok) throw new Error(`share_targets HTTP ${res.status}`)
      const data = await res.json()
      this._fillSelectOptions(this.sharePeopleSelectTarget, data.people)
      this._fillSelectOptions(this.shareGroupsSelectTarget, data.groups)
      this._fillSelectOptions(
        document.getElementById('permissions-people-select'),
        data.people
      )
      this._fillSelectOptions(
        document.getElementById('permissions-groups-select'),
        data.groups
      )
      this._shareTargetsLoaded = true
    })().finally(() => {
      this._shareTargetsPromise = null
    })

    return this._shareTargetsPromise
  }

  _fillSelectOptions(selectEl, items) {
    if (!selectEl || !Array.isArray(items)) return
    const selected = new Set(
      Array.from(selectEl.selectedOptions).map((o) => o.value)
    )
    selectEl.replaceChildren()
    for (const item of items) {
      const opt = document.createElement('option')
      opt.value = String(item.id)
      opt.textContent = item.name
      if (selected.has(opt.value)) opt.selected = true
      selectEl.appendChild(opt)
    }
  }

  // Reset share modal to initial state
  resetShareModal() {
    if (this.hasShareFormContainerTarget) this.shareFormContainerTarget.classList.remove('d-none')
    if (this.hasShareLinkContainerTarget) this.shareLinkContainerTarget.classList.add('d-none')
    if (this.hasCreateShareBtnTarget) this.createShareBtnTarget.classList.remove('d-none')
    if (this.hasShareDurationSelectTarget) this.shareDurationSelectTarget.value = '7'
    if (this.hasShareTypePublicTarget) this.shareTypePublicTarget.checked = true
    if (this.hasSharePeopleSelectTarget) this.sharePeopleSelectTarget.selectedIndex = -1
    if (this.hasShareGroupsSelectTarget) this.shareGroupsSelectTarget.selectedIndex = -1
    if (this.hasPublicShareLinkSectionTarget) this.publicShareLinkSectionTarget.classList.add('d-none')
    if (this.hasInternalShareSummaryTarget) this.internalShareSummaryTarget.classList.add('d-none')
    
    // Show public options, hide internal options
    if (this.hasPublicShareOptionsTarget) this.publicShareOptionsTarget.classList.remove('d-none')
    if (this.hasInternalShareOptionsTarget) this.internalShareOptionsTarget.classList.add('d-none')
  }

  // Setup share type toggle
  setupShareTypeToggle() {
    if (!this.hasShareTypePublicTarget || !this.hasShareTypeInternalTarget) return
    
    const toggleOptions = () => {
      if (this.shareTypePublicTarget.checked) {
        this.publicShareOptionsTarget?.classList.remove('d-none')
        this.internalShareOptionsTarget?.classList.add('d-none')
      } else {
        this.publicShareOptionsTarget?.classList.add('d-none')
        this.internalShareOptionsTarget?.classList.remove('d-none')
      }
    }
    
    this.shareTypePublicTarget.addEventListener('change', toggleOptions)
    this.shareTypeInternalTarget.addEventListener('change', toggleOptions)
  }
  
  // Create share link
  createShare(event) {
    event.preventDefault()
    
    const fileId = this.currentShareFileId
    const folderId = this.currentShareFolderId
    const button = event.currentTarget
    
    // Determine share type
    const isPublic = document.getElementById('share-type-public')?.checked
    
    // Gather share data
    const shareData = {
      is_public: isPublic
    }
    
    if (isPublic) {
      // Public share - get duration
      shareData.duration = document.getElementById('share-duration-select').value
    } else {
      // Internal share - get selected people and groups
      const peopleSelect = document.getElementById('share-people-select')
      const groupsSelect = document.getElementById('share-groups-select')
      
      const selectedPeople = Array.from(peopleSelect.selectedOptions).map(opt => opt.value)
      const selectedGroups = Array.from(groupsSelect.selectedOptions).map(opt => opt.value)
      
      if (selectedPeople.length === 0 && selectedGroups.length === 0) {
        alert(this.i18n.t('drive.select_recipients_error') || 'Please select at least one person or group')
        return
      }
      
      shareData.person_ids = selectedPeople
      shareData.person_group_ids = selectedGroups
    }
    
    // Determine URL based on whether sharing file or folder
    let shareUrl
    if (fileId) {
      shareUrl = drive_create_share_path(fileId)
    } else if (folderId) {
      shareUrl = drive_create_folder_share_path(folderId)
    } else {
      alert(this.i18n.t('drive.error'))
      return
    }
    
    // Disable button during request
    button.disabled = true
    const originalText = button.textContent
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>' + this.i18n.t('drive.creating')
    
    // Make API request
    fetch(shareUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify(shareData)
    })
    .then(async response => {
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const msg = data.message || data.error || this.i18n.t('drive.error')
        alert(msg)
        button.disabled = false
        button.textContent = originalText
        return null
      }
      return data
    })
    .then(data => {
      if (!data) return
      console.log('Share response:', data) // Debug
      
      // Hide form, show result
      document.getElementById('share-form-container').classList.add('d-none')
      document.getElementById('share-link-container').classList.remove('d-none')
      document.getElementById('create-share-btn').classList.add('d-none')
      
      // Hide both sections initially
      const publicSection = document.getElementById('public-share-link-section')
      const internalSection = document.getElementById('internal-share-summary')
      publicSection.classList.add('d-none')
      internalSection.classList.add('d-none')
      
      if (isPublic && data.share_url) {
        // Public share - show link
        publicSection.classList.remove('d-none')
        document.getElementById('share-link-input').value = data.share_url
      } else if (!isPublic && data.recipients) {
        // Internal share - show recipients summary
        internalSection.classList.remove('d-none')
        
        let recipientsHTML = '<ul class="list-unstyled mb-0">'
        if (data.recipients.people && data.recipients.people.length > 0) {
          recipientsHTML += '<li class="mb-2"><strong>' + this.i18n.t('drive.people') + ':</strong><ul>'
          data.recipients.people.forEach(person => {
            recipientsHTML += `<li>${person.name}</li>`
          })
          recipientsHTML += '</ul></li>'
        }
        if (data.recipients.groups && data.recipients.groups.length > 0) {
          recipientsHTML += '<li><strong>' + this.i18n.t('drive.groups') + ':</strong><ul>'
          data.recipients.groups.forEach(group => {
            recipientsHTML += `<li>${group.name}</li>`
          })
          recipientsHTML += '</ul></li>'
        }
        recipientsHTML += '</ul>'
        
        document.getElementById('shared-recipients-list').innerHTML = recipientsHTML
      } else {
        console.error('Unexpected response format:', data)
      }
      
      // Update row to show shared icon
      if (fileId) {
        this.updateFileRowSharedState(fileId, true)
      } else if (folderId) {
        this.updateFolderRowSharedState(folderId, true)
      }
    })
    .catch(error => {
      console.error('Share error:', error)
      alert(this.i18n.t('drive.share_creation_failed'))
    })
    .finally(() => {
      button.disabled = false
      button.textContent = this.i18n.t('drive.create_share')
    })
  }
  
  // Copy share link to clipboard
  copyShareLink(event) {
    const input = document.getElementById('share-link-input')
    input.select()
    input.setSelectionRange(0, 99999) // For mobile devices
    
    navigator.clipboard.writeText(input.value).then(() => {
      // Show success message
      const successMsg = document.getElementById('copy-success')
      successMsg.classList.remove('d-none')
      
      // Hide after 3 seconds
      setTimeout(() => {
        successMsg.classList.add('d-none')
      }, 3000)
    }).catch(err => {
      console.error('Copy failed:', err)
      alert(this.i18n.t('drive.error'))
    })
  }
  
  // Update file row to show shared indicator
  updateFileRowSharedState(fileId, isShared) {
    const row = this.element.querySelector(`tr[data-item-id="${fileId}"][data-item-type="file"]`)
    if (row) {
      const nameCell = row.querySelector('td:nth-child(3)')
      if (nameCell && isShared) {
        // Add shared icon if not already present
        if (!nameCell.querySelector('.bi-share-fill')) {
          const sharedIcon = document.createElement('i')
          sharedIcon.className = 'bi bi-share-fill text-primary ms-2'
          sharedIcon.title = 'Shared'
          nameCell.appendChild(sharedIcon)
        }
      }
    }
  }

  // Update folder row to show shared indicator
  updateFolderRowSharedState(folderId, isShared) {
    const row = this.element.querySelector(`tr[data-item-id="${folderId}"][data-item-type="folder"]`)
    if (row) {
      const nameCell = row.querySelector('td:nth-child(3)')
      if (nameCell && isShared) {
        // Add shared icon if not already present
        if (!nameCell.querySelector('.bi-share-fill')) {
          const sharedIcon = document.createElement('i')
          sharedIcon.className = 'bi bi-share-fill text-primary ms-2'
          sharedIcon.title = 'Shared'
          nameCell.appendChild(sharedIcon)
        }
      }
    }
  }

  // Manage file permissions
  async managePermissions(event) {
    const fileId = event.params.fileId
    window._driveUi?.openPermissions?.({ type: 'file', id: fileId })
  }

  // Manage folder permissions
  async manageFolderPermissions(event) {
    const folderId = event.params.folderId
    window._driveUi?.openPermissions?.({ type: 'folder', id: folderId })
  }

  // Reset permissions modal to initial state
  resetPermissionsModal() {
    const formContainer = document.getElementById('permissions-form-container')
    const resultContainer = document.getElementById('permissions-result-container')
    const saveBtn = document.getElementById('save-permissions-btn')
    const peopleSelect = document.getElementById('permissions-people-select')
    const groupsSelect = document.getElementById('permissions-groups-select')
    const clearAllCheckbox = document.getElementById('permissions-clear-all')
    
    if (formContainer) formContainer.classList.remove('d-none')
    if (resultContainer) resultContainer.classList.add('d-none')
    if (saveBtn) saveBtn.classList.remove('d-none')
    if (peopleSelect) peopleSelect.selectedIndex = -1
    if (groupsSelect) groupsSelect.selectedIndex = -1
    if (clearAllCheckbox) clearAllCheckbox.checked = false
  }

  // Load file permissions
  loadFilePermissions(fileId) {
    fetch(drive_get_file_permissions_path(fileId), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      }
    })
    .then(response => response.json())
    .then(data => {
      this.populatePermissionsSelects(data)
    })
    .catch(error => {
      console.error('Error loading permissions:', error)
    })
  }

  // Load folder permissions
  loadFolderPermissions(folderId) {
    fetch(drive_get_folder_permissions_path(folderId), {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      }
    })
    .then(response => response.json())
    .then(data => {
      this.populatePermissionsSelects(data)
    })
    .catch(error => {
      console.error('Error loading permissions:', error)
    })
  }

  // Populate permissions selects with data
  populatePermissionsSelects(data) {
    if (data.permissions) {
      // Select people
      const peopleSelect = document.getElementById('permissions-people-select')
      if (data.permissions.people && peopleSelect) {
        const peopleIds = data.permissions.people.map(p => p.id.toString())
        Array.from(peopleSelect.options).forEach(option => {
          option.selected = peopleIds.includes(option.value)
        })
      }
      
      // Select groups
      const groupsSelect = document.getElementById('permissions-groups-select')
      if (data.permissions.groups && groupsSelect) {
        const groupIds = data.permissions.groups.map(g => g.id.toString())
        Array.from(groupsSelect.options).forEach(option => {
          option.selected = groupIds.includes(option.value)
        })
      }
    }
  }

  // Save permissions (file or folder)
  savePermissions(event) {
    event.preventDefault()
    
    const button = event.currentTarget
    const clearAll = document.getElementById('permissions-clear-all').checked
    
    let person_ids = []
    let person_group_ids = []
    
    if (!clearAll) {
      const peopleSelect = document.getElementById('permissions-people-select')
      const groupsSelect = document.getElementById('permissions-groups-select')
      
      person_ids = Array.from(peopleSelect.selectedOptions).map(opt => opt.value)
      person_group_ids = Array.from(groupsSelect.selectedOptions).map(opt => opt.value)
    }
    
    // Determine URL based on type
    let url
    if (this.currentPermissionsType === 'file') {
      url = drive_set_file_permissions_path(this.currentPermissionsFileId)
    } else {
      url = drive_set_folder_permissions_path(this.currentPermissionsFolderId)
    }
    
    // Disable button during request
    button.disabled = true
    const originalText = button.textContent
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>' + this.i18n.t('drive.saving')
    
    // Make API request
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        person_ids: person_ids,
        person_group_ids: person_group_ids
      })
    })
    .then(response => response.json())
    .then(data => {
      // Hide form, show result
      document.getElementById('permissions-form-container').classList.add('d-none')
      document.getElementById('permissions-result-container').classList.remove('d-none')
      document.getElementById('save-permissions-btn').classList.add('d-none')
      
      // Show permissions summary using templates
      this.renderPermissionsSummary(data, clearAll)
    })
    .catch(error => {
      console.error('Permissions error:', error)
      alert(this.i18n.t('drive.permissions_update_failed'))
    })
    .finally(() => {
      button.disabled = false
      button.textContent = this.i18n.t('drive.save_permissions')
    })
  }

  // Render permissions summary using templates
  renderPermissionsSummary(data, clearAll) {
    const container = document.getElementById('permissions-recipients-list')
    container.innerHTML = ''
    
    // Check if all members have access
    if (clearAll || ((!data.permissions.people || data.permissions.people.length === 0) && 
                     (!data.permissions.groups || data.permissions.groups.length === 0))) {
      const template = document.getElementById('permissions-all-members-template')
      const clone = template.content.cloneNode(true)
      container.appendChild(clone)
      return
    }
    
    // Create container list
    const containerTemplate = document.getElementById('permissions-recipients-container-template')
    const containerClone = containerTemplate.content.cloneNode(true)
    const ul = containerClone.querySelector('ul')
    
    // Add people
    if (data.permissions.people && data.permissions.people.length > 0) {
      const groupTemplate = document.getElementById('permissions-group-template')
      const groupClone = groupTemplate.content.cloneNode(true)
      const strong = groupClone.querySelector('strong')
      const groupUl = groupClone.querySelector('ul')
      
      strong.textContent = this.i18n.t('drive.people') + ':'
      
      data.permissions.people.forEach(person => {
        const itemTemplate = document.getElementById('permissions-item-template')
        const itemClone = itemTemplate.content.cloneNode(true)
        const li = itemClone.querySelector('li')
        li.textContent = person.name
        groupUl.appendChild(itemClone)
      })
      
      ul.appendChild(groupClone)
    }
    
    // Add groups
    if (data.permissions.groups && data.permissions.groups.length > 0) {
      const groupTemplate = document.getElementById('permissions-group-template')
      const groupClone = groupTemplate.content.cloneNode(true)
      const strong = groupClone.querySelector('strong')
      const groupUl = groupClone.querySelector('ul')
      
      strong.textContent = this.i18n.t('drive.groups') + ':'
      
      data.permissions.groups.forEach(group => {
        const itemTemplate = document.getElementById('permissions-item-template')
        const itemClone = itemTemplate.content.cloneNode(true)
        const li = itemClone.querySelector('li')
        li.textContent = group.name
        groupUl.appendChild(itemClone)
      })
      
      ul.appendChild(groupClone)
    }
    
    container.appendChild(containerClone)
  }

  // Upload progress
  showUploadProgress(progress = 0) {
    const progressContainer = document.getElementById('upload-progress')
    if (!progressContainer) return
    
    // Create progress bar from template if it doesn't exist
    if (progressContainer.children.length === 0) {
      const template = document.getElementById('upload-progress-template')
      if (!template) {
        console.error('Upload progress template not found')
        return
      }
      const clone = template.content.cloneNode(true)
      progressContainer.appendChild(clone)
    }
    
    // Update progress
    const progressBar = progressContainer.querySelector('[data-progress-bar]')
    const progressText = progressContainer.querySelector('[data-progress-text]')
    
    if (progressBar) {
      progressBar.style.width = `${progress}%`
      progressBar.setAttribute('aria-valuenow', Math.round(progress))
    }
    
    if (progressText) {
      progressText.textContent = `${Math.round(progress)}%`
    }
  }

  hideUploadProgress() {
    const progressContainer = document.getElementById('upload-progress')
    if (progressContainer) {
      progressContainer.innerHTML = ''
    }
  }

  // Cancel upload
  cancelUpload() {
    this.shouldCancelUpload = true
    this.uploadingFiles.clear()
  }

  // Download file
  downloadFile(event) {
    const fileId = event.params.fileId
    window.location.href = drive_download_file_path(fileId)
  }

  // Download entire folder as ZIP
  downloadFolder(event) {
    const folderId = event.params.folderId
    window.location.href = drive_download_folder_path(folderId)
  }

  // Upload a directory (folder) — triggered by a hidden <input type="file" webkitdirectory>
  // Each file carries file.webkitRelativePath (e.g. "MyFolder/sub/file.txt").
  // Uses the existing upload modal for progress tracking.
  async uploadFolder(event) {
    const allFiles = Array.from(event.target.files)
    event.target.value = ''
    // Skip hidden files (e.g. .DS_Store)
    const files = allFiles.filter(f => !f.name.startsWith('.'))
    if (!files.length) return

    const directUploadUrl = rails_direct_uploads_path()
    const currentFolderId = this._resolveCurrentFolderId() || ''

    // Initialise modal with all files
    this.initializeUpload(files)

    const signedIds  = []
    const folderPaths = []

    for (const file of files) {
      const relativePath = file.webkitRelativePath || file.name
      const fileId = file.fileId

      this.updateFileProgress(fileId, 0, 'uploading')

      try {
        const blob = await new Promise((resolve, reject) => {
          const self = this
          const upload = new DirectUpload(file, directUploadUrl, {
            directUploadWillStoreFileWithXHR(xhr) {
              xhr.upload.addEventListener('progress', ev => {
                if (ev.lengthComputable) {
                  const pct = Math.round((ev.loaded / ev.total) * 95)
                  self.updateFileProgress(fileId, pct, 'uploading')
                }
              })
            }
          })
          upload.create((error, b) => error ? reject(error) : resolve(b))
        })

        signedIds.push(blob.signed_id)
        folderPaths.push(relativePath)
        this.completeFile(fileId, true)
      } catch (err) {
        console.error('Direct upload failed:', err)
        this.completeFile(fileId, false)
      }
    }

    // All direct uploads done — now register them server-side
    const formData = new FormData()
    signedIds.forEach(id => formData.append('drive_files[]', id))
    folderPaths.forEach(p => formData.append('folder_paths[]', p))
    if (currentFolderId) formData.append('folder_id', currentFolderId)

    await this._attachFolderUploads(formData)
  }

  // Returns true if any DataTransferItemList item is a directory.
  // Recursively collect all files + relative paths from pre-collected FileSystemEntry list,
  // then upload them the same way as uploadFolder().
  async _uploadDroppedFolderEntries(entries) {
    const collected = []
    await Promise.all(entries.map(entry => this._collectEntry(entry, '', collected)))
    if (!collected.length) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const directUploadUrl = rails_direct_uploads_path()
    const currentFolderId = this._resolveCurrentFolderId() || ''

    this.initializeUpload(collected.map(({ file }) => file))

    const signedIds   = []
    const folderPaths = []

    for (const { file, relativePath } of collected) {
      const fileId = file.fileId
      this.updateFileProgress(fileId, 0, 'uploading')

      try {
        const blob = await new Promise((resolve, reject) => {
          const self = this
          const upload = new DirectUpload(file, directUploadUrl, {
            directUploadWillStoreFileWithXHR(xhr) {
              xhr.upload.addEventListener('progress', ev => {
                if (ev.lengthComputable) {
                  self.updateFileProgress(fileId, Math.round((ev.loaded / ev.total) * 95), 'uploading')
                }
              })
            }
          })
          upload.create((error, b) => error ? reject(error) : resolve(b))
        })
        signedIds.push(blob.signed_id)
        folderPaths.push(relativePath)
        this.completeFile(fileId, true)
      } catch (err) {
        console.error('Direct upload failed:', err)
        this.completeFile(fileId, false)
      }
    }

    const formData = new FormData()
    signedIds.forEach(id => formData.append('drive_files[]', id))
    folderPaths.forEach(p => formData.append('folder_paths[]', p))
    if (currentFolderId) formData.append('folder_id', currentFolderId)

    await this._attachFolderUploads(formData)
  }

  async _attachFolderUploads(formData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    this._attachInProgress = true
    this.store?.setUploadState?.({ serverProcessing: true })
    try {
      // BC virtual folders have no nested DriveFolder tree — flatten onto BuildingComplex#files.
      const bcId = this._resolveBuildingComplexId()
      let body = formData
      let url = drive_attach_folder_uploads_path()
      if (bcId) {
        url = this._buildingComplexAttachUploadsUrl(bcId)
        body = new FormData()
        for (const id of formData.getAll('drive_files[]')) {
          body.append('drive_files[]', id)
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body
      })
      if (response.ok) {
        // Reload the full folder to get all file metadata
        if (this.store) {
          const folderPath = window.location.pathname + '.json' + window.location.search
          await this.store.loadFolder(folderPath)
        }
      } else {
        const quota = await parseQuotaResponse(response)
        if (quota) {
          this.onQuotaExceeded?.(quota.raw ?? quota)
          window._driveUi?.reportQuotaExceeded?.(quota.raw ?? quota)
          return
        }
        console.error('Folder attach error:', response.status, response.statusText)
      }
    } catch (err) {
      console.error('Folder attach failed:', err)
    } finally {
      this._attachInProgress = false
      this.store?.setUploadState?.({ serverProcessing: false })
      this.closeUploadModal()
      this.uploadedFiles.clear()
      this.totalFiles = 0
      this.completedFiles = 0
      this.failedFiles = 0
    }
  }

  // Recursively walk a FileSystemEntry, appending { file, relativePath } to results.
  // prefix tracks the path of the PARENT directory relative to the drop root.
  _collectEntry(entry, prefix, results) {
    return new Promise(resolve => {
      if (entry.isFile) {
        // Skip hidden files (e.g. .DS_Store, .gitignore)
        if (entry.name.startsWith('.')) return resolve()
        entry.file(file => {
          const relativePath = prefix ? `${prefix}/${file.name}` : file.name
          results.push({ file, relativePath })
          resolve()
        }, () => resolve())
      } else if (entry.isDirectory) {
        // Skip hidden directories
        if (entry.name.startsWith('.')) return resolve()
        const reader = entry.createReader()
        const readAll = (acc) => {
          reader.readEntries(async batch => {
            if (!batch.length) {
              await Promise.all(
                acc.map(child => {
                  const childPrefix = prefix ? `${prefix}/${entry.name}` : entry.name
                  return this._collectEntry(child, childPrefix, results)
                })
              )
              resolve()
            } else {
              readAll([...acc, ...batch])
            }
          }, () => resolve())
        }
        readAll([])
      } else {
        resolve()
      }
    })
  }

  // Delete single file
  deleteFile(event) {
    const fileId = event.params.fileId
    
    if (!confirm(this.i18n.t('drive.confirm_delete_file'))) return
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    
    fetch(drive_delete_file_path(fileId), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => {
      if (response.ok) {
        if (this.store) {
          this.store.removeFile(parseInt(fileId, 10))
        } else {
          window.location.reload()
        }
      } else {
        throw new Error('Delete failed')
      }
    })
    .catch(error => {
      console.error('Error deleting file:', error)
      alert(this.i18n.t('drive.error'))
    })
  }

  // Delete single folder
  deleteFolder(event) {
    const folderId = event.params.folderId

    if (!confirm(this.i18n.t('drive.confirm_delete_folder_with_content'))) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    fetch(drive_path(folderId), {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken
      }
    })
    .then(response => {
      // OK response OR async deletion (202) = success
      if (response.ok || response.status === 202) {
        // For async deletions, the response may be JSON with async: true
        return response.json().catch(() => ({}))
      }
      throw new Error('Delete failed')
    })
    .then(data => {
      // If there was a JSON response (even empty), use it.
      // For large folders, backend returns immediately with async: true
      // and the job processes in background.
      if (this.store) {
        this.store.removeFolder(parseInt(folderId, 10))
      } else {
        window.location.reload()
      }
    })
    .catch(error => {
      console.error('Error deleting folder:', error)
      alert(this.i18n.t('drive.error'))
    })
  }
  
  // Upload modal — state lives in drive-store signals (UploadModal.jsx)
  initializeUpload(files) {
    if (!files || files.length === 0) return

    this.totalFiles = files.length
    this.completedFiles = 0
    this.failedFiles = 0
    this.uploadedFiles.clear()
    this.isUploading = true

    files.forEach((file, index) => {
      file.fileId = this.addFileToModalList(file, index)
    })

    this.showUploadModal()
    this.updateOverallProgress()
  }

  _pushUploadStore(extra = {}) {
    if (!this.store?.setUploadState) return
    const list = [...this.uploadedFiles.entries()].map(([id, info]) => ({
      id,
      name: info.name,
      size: info.size,
      status: info.status === 'completed' ? 'done' : info.status === 'failed' ? 'error' : info.status,
      progress: info.progress || 0
    }))
    this.store.setUploadState({
      files: list,
      overallPercent: this._overallPercent(),
      statusText: this.getOverallStatusText(),
      ...extra
    })
  }

  _overallPercent() {
    let sum = 0
    this.uploadedFiles.forEach((info) => { sum += info.progress || 0 })
    return this.totalFiles > 0 ? Math.round(sum / this.totalFiles) : 0
  }

  addFileToModalList(file, index) {
    const fileId = `file-${index}-${Date.now()}`
    this.uploadedFiles.set(fileId, {
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0
    })
    return fileId
  }

  updateFileProgress(fileId, progress, status = 'uploading') {
    if (!this.uploadedFiles.has(fileId)) return
    const fileInfo = this.uploadedFiles.get(fileId)
    fileInfo.progress = progress
    fileInfo.status = status
    this.updateOverallProgress()
  }

  completeFile(fileId, success = true) {
    if (!this.uploadedFiles.has(fileId)) return
    const fileInfo = this.uploadedFiles.get(fileId)
    fileInfo.status = success ? 'completed' : 'failed'
    fileInfo.progress = 100
    if (success) this.completedFiles++
    else this.failedFiles++
    this.updateOverallProgress()
  }

  updateOverallProgress() {
    this._pushUploadStore()
    if (this.completedFiles + this.failedFiles === this.totalFiles) {
      this.uploadComplete()
    }
  }

  getOverallStatusText() {
    const remaining = this.totalFiles - this.completedFiles - this.failedFiles
    if (remaining === 0) {
      if (this.failedFiles === 0) return this.i18n.t('drive.all_files_uploaded')
      if (this.completedFiles === 0) return this.i18n.t('drive.all_uploads_failed')
      return `${this.completedFiles} ${this.i18n.t('drive.uploaded')}, ${this.failedFiles} ${this.i18n.t('drive.failed')}`
    }
    return `${this.completedFiles} ${this.i18n.t('drive.uploaded')}, ${remaining} ${this.i18n.t('drive.remaining')}`
  }

  uploadComplete() {
    this.isUploading = false
    this._pushUploadStore()
    if (this._attachInProgress) return
    setTimeout(() => {
      this.closeUploadModal()
      this.uploadedFiles.clear()
      this.totalFiles = 0
      this.completedFiles = 0
      this.failedFiles = 0
    }, 2000)
  }

  showUploadModal() {
    this._pushUploadStore({ open: true, serverProcessing: false })
  }

  closeUploadModal() {
    this.store?.setUploadState?.({ open: false, serverProcessing: false, files: [], overallPercent: 0, statusText: '' })
  }

  cancelUploadModal() {
    this.cancelUpload()
    this.closeUploadModal()
  }
  
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  getStatusText(status) {
    const statusMap = {
      'pending': this.i18n.t('drive.pending'),
      'uploading': this.i18n.t('drive.uploading'),
      'completed': this.i18n.t('drive.completed'),
      'failed': this.i18n.t('drive.failed')
    }
    return statusMap[status] || status
  }

  getStatusClass(status) {
    const classMap = {
      'pending': 'bg-secondary',
      'uploading': 'bg-primary',
      'completed': 'bg-success',
      'failed': 'bg-danger'
    }
    return classMap[status] || 'bg-secondary'
  }

  // Syncs selection count text; adaptive toolbar owns the selection UI.
  _updateActionBarFromStore(count) {
    const i18n = this.i18n
    const text = `${count} ${i18n.t('drive.items_selected')}`

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = text
    }
    const selectionCountEl = this.element.querySelector('[data-drive-target="selectionCount"]')
    if (selectionCountEl) {
      selectionCountEl.textContent = text
    }

    if (this.hasDriveToolbarOutlet) {
      this.driveToolbarOutlet.setSelectionMode(count)
    }
  }

  // Reactively updates #drive-breadcrumb from drive store signals (SPA folder nav).
  _updateLayoutBreadcrumb(crumbs, currentFolder) {
    const nav = this.hasBreadcrumbTarget
      ? this.breadcrumbTarget
      : document.querySelector('#drive-breadcrumb ol.breadcrumb')
    if (!nav || crumbs.length === 0) return

    nav.innerHTML = ''

    crumbs.forEach((crumb, i) => {
      const isLast = i === crumbs.length - 1
      const li = document.createElement('li')
      li.className = 'breadcrumb-item text-truncate' + (isLast ? ' active' : '')
      if (isLast) {
        li.setAttribute('aria-current', 'page')
        const icon = i === 0 ? 'bi-cloud' : 'bi-folder-fill text-warning'
        li.innerHTML = `<i class="bi ${icon} me-1"></i>${crumb.name}`
      } else {
        const a = document.createElement('a')
        const icon = i === 0 ? 'bi-cloud' : 'bi-folder-fill text-warning'
        a.innerHTML = `<i class="bi ${icon} me-1"></i>${crumb.name}`
        a.href = crumb.path
        a.addEventListener('click', (e) => {
          e.preventDefault()
          this.store.loadFolder(crumb.path + '.json')
          history.pushState({}, '', crumb.path)
        })
        li.appendChild(a)
      }
      nav.appendChild(li)
    })
  }

  // Numeric DriveFolder id only — virtual folder ids ("tasks", "bc-12") are ignored.
  _numericFolderId(id) {
    if (id == null || id === '') return null
    const str = String(id)
    return /^\d+$/.test(str) ? str : null
  }

  // BC / tasks / equipment virtual folder contexts (no nested DriveFolder tree).
  _isVirtualFolderContext(folder) {
    if (!folder) return false
    // BC entries open a real DriveFolder tree — only tasks/equipment stay virtual.
    const id = String(folder.id || '')
    return id === 'tasks' || id === 'equipments' || id.startsWith('tasks-')
  }

  // Only the root virtual `bc-:id` entry — real BC DriveFolders use numeric ids + folder_id.
  _resolveBuildingComplexId() {
    const folder = this.store?.currentFolder?.value
    if (!folder) return null
    const match = String(folder.id || '').match(/^bc-(\d+)$/)
    return match ? match[1] : null
  }

  _buildingComplexAttachUploadsUrl(bcId) {
    return `/drive/building_complexes/${bcId}/attach_uploads`
  }

  // Regular DriveFolder attach vs BC virtual-folder attach.
  _attachUploadsUrl() {
    const bcId = this._resolveBuildingComplexId()
    if (bcId) return this._buildingComplexAttachUploadsUrl(bcId)
    return attach_uploads_drive_index_path()
  }

  // Prefer live store folder (SPA navigation), then SSR Stimulus values.
  _resolveCurrentFolderId() {
    const fromStore = this._numericFolderId(this.store?.currentFolder?.value?.id)
    if (fromStore) return fromStore
    return this._numericFolderId(this.currentFolderValue)
      || this._numericFolderId(this.folderIdValue)
      || this._numericFolderId(this.element.dataset.currentFolderId)
      || null
  }
}
